import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import {
  readPersistedCompanyLogoUrl,
  TenantService,
  writePersistedCompanyLogoUrl,
} from '../../../core';
import type { Vertical } from '../../../core/enums/verticals';
import type { Company } from '../../../shared/models';
import type { CompanyMembership, ResolvedCompanyRouteContext } from '../models/company.models';
import { normalizeCompanyMembershipRole } from '../utils/company-membership-role.util';
import { CompanyApiService } from '../services/company-api.service';
import { CompanyVerticalApiService } from '../services/company-vertical-api.service';

const ACTIVE_COMPANY_STORAGE_KEY = 'taskeo.company.active-company-id';
const ACTIVE_COMPANY_KEY_STORAGE_KEY = 'taskeo.company.active-company-key';
const AUTH_USER_STORAGE_KEY = 'taskeo.auth.user';

export type CompanyRouteResolveResult = 'ok' | 'not-found' | 'forbidden' | 'error';

@Injectable({ providedIn: 'root' })
export class CompanyStore {
  private readonly router = inject(Router);
  private readonly tenant = inject(TenantService);
  private readonly api = inject(CompanyApiService);
  private readonly verticalApi = inject(CompanyVerticalApiService);

  /**
   * Evita chamar resolve-by-key em toda navegação quando a API não devolve logo;
   * após a primeira tentativa nesta sessão, mantemos o resultado (com ou sem logo).
   */
  private readonly routeLogoAttemptedForCompanyIds = new Set<string>();

  /**
   * Chaves de rota já resolvidas com sucesso nesta sessão (lowercase).
   * Evita chamadas repetidas à API em cada navegação de rota filha quando
   * a companyKey já foi validada pelo menos uma vez.
   */
  private readonly resolvedRouteKeys = new Set<string>();

  private readonly membershipsSignal = signal<CompanyMembership[]>([]);
  private readonly activeCompanyIdSignal = signal<string | null>(null);
  private readonly loadingSignal = signal(false);
  private readonly errorSignal = signal<string | null>(null);

  readonly memberships = this.membershipsSignal.asReadonly();
  readonly activeCompanyId = this.activeCompanyIdSignal.asReadonly();
  readonly loading = this.loadingSignal.asReadonly();
  readonly error = this.errorSignal.asReadonly();

  /**
   * Preferência: membership carregada. Se o utilizador tem empresa ativa no tenant mas ainda não há
   * linha em `memberships` (ex.: sync atrasado), derivamos do tenant — senão `isAdmin` e o link
   * "Membros" no menu do processo ficam incorretos.
   */
  readonly activeCompany = computed((): CompanyMembership | null => {
    const id = this.activeCompanyId();
    if (!id) return null;
    const fromList = this.memberships().find((m) => m.companyId === id);
    if (fromList) return fromList;

    const tenantCompany = this.tenant.companies().find((c) => c.id === id);
    if (!tenantCompany) return null;

    return {
      companyId: tenantCompany.id,
      companyKey: tenantCompany.companyKey?.trim() || this.slugify(tenantCompany.name),
      companyName: tenantCompany.name,
      companyLogo: tenantCompany.logoUrl?.trim() || null,
      role: normalizeCompanyMembershipRole(tenantCompany.role),
      status: 'Active',
    };
  });

  readonly activeCompanyKey = computed(() => {
    const membership = this.activeCompany();
    if (!membership) return null;
    const key = membership.companyKey?.trim();
    return key || this.slugify(membership.companyName) || null;
  });

  async loadMemberships(): Promise<void> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    try {
      const rawMemberships = await this.api.getMemberships();
      const prevLogoById = new Map(
        this.tenant.companies().map((c) => [c.id, c.logoUrl?.trim() || null] as const)
      );
      const memberships = rawMemberships.map((m) => {
        const fromApi = m.companyLogo?.trim() || null;
        const merged =
          fromApi ||
          readPersistedCompanyLogoUrl(m.companyId) ||
          prevLogoById.get(m.companyId)?.trim() ||
          null;
        return { ...m, companyLogo: merged };
      });
      for (const m of memberships) {
        if (m.companyLogo?.trim()) {
          writePersistedCompanyLogoUrl(m.companyId, m.companyLogo);
        }
      }

      const tenantCompanies = this.tenant.companies();
      const tenantActiveCompanyId = this.tenant.getActiveCompanyId();

      // Alguns ambientes retornam 200 com lista vazia até o vínculo ser propagado.
      // Nesse caso preservamos o tenant atual para não quebrar o fluxo de login.
      if (memberships.length === 0 && tenantCompanies.length > 0 && tenantActiveCompanyId) {
        const fallbackMemberships = tenantCompanies.map((company) => ({
          companyId: company.id,
          companyKey: company.companyKey?.trim() || this.slugify(company.name),
          companyName: company.name,
          companyLogo: company.logoUrl ?? null,
          role: normalizeCompanyMembershipRole(company.role, 'Member'),
          status: 'Active',
        }));
        this.membershipsSignal.set(fallbackMemberships);
        this.activeCompanyIdSignal.set(tenantActiveCompanyId);
        this.persistActiveCompanyId(tenantActiveCompanyId);
        this.syncPersistedCompanyKeyForActive();
        return;
      }

      if (memberships.length === 0) {
        const sessionFallback = this.getSessionFallbackMembership();
        if (sessionFallback) {
          this.membershipsSignal.set([sessionFallback]);
          this.activeCompanyIdSignal.set(sessionFallback.companyId);
          this.tenant.setCompanies([
            {
              id: sessionFallback.companyId,
              companyKey: sessionFallback.companyKey,
              name: sessionFallback.companyName,
              slug: sessionFallback.companyName,
              role: sessionFallback.role,
              logoUrl: sessionFallback.companyLogo ?? undefined,
              language: null,
            },
          ]);
          this.tenant.setActiveCompanyById(sessionFallback.companyId);
          this.persistActiveCompanyId(sessionFallback.companyId);
          this.persistActiveCompanyKey(sessionFallback.companyKey);
          return;
        }
      }

      this.membershipsSignal.set(memberships);

      const companies = memberships.map((membership) => this.toCompany(membership));
      this.tenant.setCompanies(companies);

      const persistedId = this.readPersistedCompanyId();
      const nextActiveCompanyId = this.resolveNextActiveCompanyId(memberships, persistedId);
      this.setActiveCompany(nextActiveCompanyId);
    } catch {
      // Fallback resiliente: se o backend de memberships falhar,
      // preserva o contexto já conhecido da sessão para não derrubar o login.
      this.errorSignal.set('Nao foi possivel carregar empresas do usuario.');
      const tenantCompanies = this.tenant.companies();
      const tenantActiveCompanyId = this.tenant.getActiveCompanyId();

      if (tenantCompanies.length > 0 && tenantActiveCompanyId) {
        const fallbackMemberships = tenantCompanies.map((company) => ({
          companyId: company.id,
          companyKey: company.companyKey?.trim() || this.slugify(company.name),
          companyName: company.name,
          companyLogo: company.logoUrl ?? null,
          role: normalizeCompanyMembershipRole(company.role, 'Member'),
          status: 'Active',
        }));
        this.membershipsSignal.set(fallbackMemberships);
        this.activeCompanyIdSignal.set(tenantActiveCompanyId);
        this.persistActiveCompanyId(tenantActiveCompanyId);
        this.syncPersistedCompanyKeyForActive();
      } else {
        const sessionFallback = this.getSessionFallbackMembership();
        if (sessionFallback) {
          this.membershipsSignal.set([sessionFallback]);
          this.activeCompanyIdSignal.set(sessionFallback.companyId);
          this.tenant.setCompanies([
            {
              id: sessionFallback.companyId,
              companyKey: sessionFallback.companyKey,
              name: sessionFallback.companyName,
              slug: sessionFallback.companyName,
              role: sessionFallback.role,
              logoUrl: sessionFallback.companyLogo ?? undefined,
              language: null,
            },
          ]);
          this.tenant.setActiveCompanyById(sessionFallback.companyId);
          this.persistActiveCompanyId(sessionFallback.companyId);
          this.persistActiveCompanyKey(sessionFallback.companyKey);
        } else {
          this.membershipsSignal.set([]);
          this.activeCompanyIdSignal.set(null);
          this.tenant.init([]);
          this.clearPersistedCompanyId();
          this.clearPersistedCompanyKey();
        }
      }
    } finally {
      this.loadingSignal.set(false);
    }
  }

  setActiveCompany(companyId: string | null): void {
    if (!companyId) {
      this.activeCompanyIdSignal.set(null);
      this.tenant.setActiveCompany(null);
      this.clearPersistedCompanyId();
      this.clearPersistedCompanyKey();
      return;
    }

    const membership = this.memberships().find((item) => item.companyId === companyId) ?? null;
    if (!membership) {
      const tenantCompany = this.tenant.companies().find((item) => item.id === companyId) ?? null;
      if (!tenantCompany) return;
      this.activeCompanyIdSignal.set(companyId);
      this.tenant.setActiveCompanyById(companyId);
      this.persistActiveCompanyId(companyId);
      const key = tenantCompany.companyKey?.trim();
      if (key) this.persistActiveCompanyKey(key);
      return;
    }

    this.activeCompanyIdSignal.set(companyId);
    this.tenant.setActiveCompanyById(companyId);
    this.persistActiveCompanyId(companyId);
    this.syncPersistedCompanyKeyForActive();
  }

  setActiveCompanyFromRoute(companyKey: string): boolean {
    const membership =
      this.memberships().find((item) => item.companyKey === companyKey) ??
      this.memberships().find((item) => this.slugify(item.companyName) === companyKey) ??
      null;
    if (!membership) return false;
    this.setActiveCompany(membership.companyId);
    return true;
  }

  switchCompany(companyId: string): boolean {
    const before = this.activeCompanyId();
    this.setActiveCompany(companyId);
    return this.activeCompanyId() !== null && this.activeCompanyId() !== before;
  }

  async ensureValidCompanyContext(preferredCompanyKeyOrId?: string | null): Promise<string | null> {
    if (!this.memberships().length) {
      await this.loadMemberships();
    }
    const memberships = this.memberships();
    if (!memberships.length) {
      const fallbackCompanyId =
        preferredCompanyKeyOrId ?? this.activeCompanyId() ?? this.tenant.getActiveCompanyId();
      if (!fallbackCompanyId) return null;
      this.setActiveCompany(fallbackCompanyId);
      return this.activeCompanyId() ?? this.tenant.getActiveCompanyId();
    }

    const preferred = preferredCompanyKeyOrId?.trim() || null;
    const preferredByKey = preferred
      ? memberships.find((item) => item.companyKey === preferred) ??
        memberships.find((item) => this.slugify(item.companyName) === preferred)
      : null;
    const preferredById =
      preferred && memberships.some((item) => item.companyId === preferred) ? preferred : null;

    const candidate =
      preferredByKey?.companyId ??
      preferredById ??
      this.activeCompanyId() ??
      this.resolveNextActiveCompanyId(memberships, this.readPersistedCompanyId());

    this.setActiveCompany(candidate);
    return this.activeCompanyId();
  }

  async redirectToActiveCompanyRoute(fallbackPath = 'dashboard'): Promise<boolean> {
    const activeCompanyId = await this.ensureValidCompanyContext();
    if (!activeCompanyId) return false;
    const key = this.activeCompanyKey();
    if (!key) return false;
    await this.router.navigate(['/c', key, fallbackPath]);
    return true;
  }

  /**
   * Resolve tenant a partir do segmento público da URL (`companyKey`).
   */
  async resolveFromRouteKey(companyKey: string): Promise<CompanyRouteResolveResult> {
    const trimmed = companyKey.trim();
    if (!trimmed) return 'error';

    const keyLower = trimmed.toLowerCase();

    // Fast path: se esta chave de rota já foi resolvida com sucesso nesta sessão
    // E a empresa ativa corresponde, pula a chamada API completamente.
    const active = this.activeCompany();
    const routeMatchesActive =
      !!active &&
      !!this.activeCompanyId() &&
      (active.companyKey?.toLowerCase() === keyLower ||
        this.slugify(active.companyName) === keyLower);

    if (routeMatchesActive) {
      this.resolvedRouteKeys.add(keyLower);
      // Carrega logo em background sem bloquear navegação (somente na primeira tentativa por sessão)
      if (!active.companyLogo?.trim() && !this.routeLogoAttemptedForCompanyIds.has(active.companyId)) {
        this.routeLogoAttemptedForCompanyIds.add(active.companyId);
        void (async () => {
          try {
            const ctx = await this.api.resolveCompanyByKey(trimmed);
            if (ctx.hasAccess) this.applyResolvedRouteContext(ctx);
          } catch { /* logo indisponível, não é crítico */ }
        })();
      }
      return 'ok';
    }

    // Chave já resolvida nesta sessão — cobre janela de inicialização onde
    // activeCompany() ainda não atualizou mas a empresa já é válida.
    if (this.resolvedRouteKeys.has(keyLower)) {
      return 'ok';
    }

    try {
      const ctx = await this.api.resolveCompanyByKey(trimmed);
      if (!ctx.hasAccess) return 'forbidden';
      this.applyResolvedRouteContext(ctx);
      this.routeLogoAttemptedForCompanyIds.add(ctx.companyId);
      this.resolvedRouteKeys.add(keyLower);
      return 'ok';
    } catch (e) {
      if (e instanceof HttpErrorResponse) {
        if (e.status === 404) return 'not-found';
        if (e.status === 403) return 'forbidden';
      }
      return 'error';
    }
  }

  resetContext(): void {
    this.routeLogoAttemptedForCompanyIds.clear();
    this.resolvedRouteKeys.clear();
    this.membershipsSignal.set([]);
    this.activeCompanyIdSignal.set(null);
    this.loadingSignal.set(false);
    this.errorSignal.set(null);
    this.tenant.init([]);
    this.clearPersistedCompanyId();
    this.clearPersistedCompanyKey();
  }

  /**
   * Aplica vertical conhecida (do GET /api/companies ou após PUT bem-sucedido).
   * Aceita só estados conhecidos: `Vertical` ou `null` explícito. Quem chama com
   * `undefined` deve simplesmente não invocar este método (preserva estado inicial).
   */
  applyCompanyVertical(companyId: string, vertical: Vertical | null): void {
    const id = companyId.trim();
    if (!id) return;
    this.membershipsSignal.update((list) =>
      list.map((m) => (m.companyId === id ? { ...m, vertical } : m))
    );
    this.tenant.setCompanies(this.memberships().map((m) => this.toCompany(m)));
  }

  /**
   * Hidrata `vertical` da Company ativa via GET /api/companies. Chamado no bootstrap
   * (após `loadMemberships`) e após troca de tenant. Silencioso em erro/DTO antigo —
   * o computed do modal só dispara com `vertical === null` explícito.
   */
  async hydrateActiveCompanyVertical(): Promise<void> {
    const activeId = this.activeCompanyId();
    if (!activeId) return;
    try {
      const result = await this.verticalApi.getCurrentVertical(activeId);
      if (result === undefined) return; // DTO antigo / fetch falhou — preserva undefined.
      this.applyCompanyVertical(activeId, result);
    } catch {
      // Defensivo: nunca derruba o shell.
    }
  }

  /**
   * Após salvar em Definições > Geral: atualiza memberships + tenant para a sidebar
   * (logo / nome) sem recarregar a página. O `companyKey` é opaco e não muda no
   * rename, então não é tocado aqui.
   */
  applyCompanyProfileFromSettings(
    companyId: string,
    patch: { logoUrl: string | null; tradeName?: string }
  ): void {
    const id = companyId.trim();
    if (!id) return;
    const logo = patch.logoUrl?.trim() || null;
    const tradeName = patch.tradeName?.trim();

    this.membershipsSignal.update((list) =>
      list.map((m) => {
        if (m.companyId !== id) return m;
        let next: CompanyMembership = { ...m, companyLogo: logo };
        if (tradeName) {
          next = { ...next, companyName: tradeName };
        }
        return next;
      })
    );

    this.tenant.setCompanies(this.memberships().map((m) => this.toCompany(m)));
  }

  private resolveNextActiveCompanyId(
    memberships: CompanyMembership[],
    persistedId: string | null
  ): string | null {
    if (!memberships.length) return null;

    const activeMemberships = memberships.filter((item) => this.isMembershipActive(item));
    const source = activeMemberships.length ? activeMemberships : memberships;

    const persistedKey = this.readPersistedCompanyKey();
    if (persistedKey) {
      const byKey = source.find((item) => item.companyKey === persistedKey);
      if (byKey) return byKey.companyId;
    }

    if (persistedId && source.some((item) => item.companyId === persistedId)) {
      return persistedId;
    }
    return source[0]?.companyId ?? null;
  }

  private applyResolvedRouteContext(ctx: ResolvedCompanyRouteContext): void {
    const list = this.memberships();
    const idx = list.findIndex((m) => m.companyId === ctx.companyId);
    const existing = idx >= 0 ? list[idx] : null;
    const prevLogo = existing?.companyLogo?.trim() || null;
    const ctxLogo = ctx.logoUrl?.trim() || null;
    const companyLogo =
      ctxLogo || prevLogo || readPersistedCompanyLogoUrl(ctx.companyId) || null;

    // /api/companies/context/:key agora lê tradeName / legalName / razaoSocial /
    // displayName etc.; só cai em `prettifyRouteKey(companyKey)` quando o payload
    // realmente não traz nenhum nome utilizável. Se o nome existente for igual ao
    // prettify do slug (sinal de boot anterior sem dados reais) e o ctx trouxer
    // algo melhor, sobrescreve — evita ficar travado em "Stonetelemaco" quando o
    // banco já tem legalName "Stone Telemaco".
    const existingName = existing?.companyName?.trim() ?? '';
    const ctxName = ctx.name?.trim() ?? '';
    const prettifiedKey = this.prettifyRouteKey(ctx.companyKey).trim();
    const existingIsPrettifiedFallback =
      !!existingName && existingName.toLowerCase() === prettifiedKey.toLowerCase();
    const ctxIsBetterThanPrettifiedFallback =
      !!ctxName && ctxName.toLowerCase() !== prettifiedKey.toLowerCase();
    const companyName =
      existingIsPrettifiedFallback && ctxIsBetterThanPrettifiedFallback
        ? ctxName
        : existingName || ctxName;

    const membership: CompanyMembership = {
      companyId: ctx.companyId,
      companyKey: ctx.companyKey,
      companyName,
      companyLogo,
      role: ctx.role,
      status: 'Active',
    };
    const nextMemberships =
      idx >= 0 ? list.map((m, i) => (i === idx ? { ...m, ...membership } : m)) : [...list, membership];
    this.membershipsSignal.set(nextMemberships);
    this.tenant.setCompanies(nextMemberships.map((m) => this.toCompany(m)));
    this.setActiveCompany(ctx.companyId);
    if (companyLogo?.trim()) {
      writePersistedCompanyLogoUrl(ctx.companyId, companyLogo);
    }
  }

  private syncPersistedCompanyKeyForActive(): void {
    const key = this.activeCompanyKey();
    if (key) this.persistActiveCompanyKey(key);
  }

  private toCompany(membership: CompanyMembership): Company {
    return {
      id: membership.companyId,
      companyKey: membership.companyKey?.trim() || this.slugify(membership.companyName),
      name: membership.companyName,
      slug: this.slugify(membership.companyName),
      role: membership.role,
      logoUrl: membership.companyLogo ?? undefined,
      language: null,
      // Preserva undefined (não-conhecido) vs null (explícito) — sem coerção.
      vertical: membership.vertical,
    };
  }

  private isMembershipActive(membership: CompanyMembership): boolean {
    return membership.status.trim().toLowerCase() === 'active';
  }

  private slugify(value: string): string {
    return value
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  }

  /**
   * Espelha `CompanyApiService.prettifyRouteKey` \u2014 usado para detectar quando o
   * `companyName` cacheado \u00e9 s\u00f3 a slug capitalizada (fallback hist\u00f3rico) e o
   * `ctx.name` novo vale mais. Mantenha em sincronia.
   */
  private prettifyRouteKey(key: string): string {
    const trimmed = key?.trim() ?? '';
    if (!trimmed) return '';
    return trimmed
      .split(/[-_/]+/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(' ');
  }

  private readPersistedCompanyId(): string | null {
    try {
      return localStorage.getItem(ACTIVE_COMPANY_STORAGE_KEY);
    } catch {
      return null;
    }
  }

  private persistActiveCompanyId(companyId: string): void {
    try {
      localStorage.setItem(ACTIVE_COMPANY_STORAGE_KEY, companyId);
    } catch {}
  }

  private clearPersistedCompanyId(): void {
    try {
      localStorage.removeItem(ACTIVE_COMPANY_STORAGE_KEY);
    } catch {}
  }

  private readPersistedCompanyKey(): string | null {
    try {
      return localStorage.getItem(ACTIVE_COMPANY_KEY_STORAGE_KEY);
    } catch {
      return null;
    }
  }

  private persistActiveCompanyKey(companyKey: string): void {
    try {
      localStorage.setItem(ACTIVE_COMPANY_KEY_STORAGE_KEY, companyKey);
    } catch {}
  }

  private clearPersistedCompanyKey(): void {
    try {
      localStorage.removeItem(ACTIVE_COMPANY_KEY_STORAGE_KEY);
    } catch {}
  }

  private getSessionFallbackMembership(): CompanyMembership | null {
    const user = this.readStoredAuthUser();
    const companyId = this.readString(user['companyId']);
    const companyName = this.readString(user['companyName']);
    const role = normalizeCompanyMembershipRole(
      user['roleValue'] ??
        user['RoleValue'] ??
        user['companyRoleValue'] ??
        user['CompanyRoleValue'] ??
        user['role'] ??
        user['Role']
    );
    if (!companyId || !companyName) return null;
    return {
      companyId,
      companyKey: this.readString(user['companyKey']) || this.slugify(companyName),
      companyName,
      companyLogo: null,
      role,
      status: 'Active',
    };
  }

  private readStoredAuthUser(): Record<string, unknown> {
    try {
      const raw = localStorage.getItem(AUTH_USER_STORAGE_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {};
    } catch {
      return {};
    }
  }

  private readString(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
  }
}
